import type { ValidationResult } from "@react-types/shared";
import { clsx } from "clsx";
import {
  CheckboxGroup as ReactAriaCheckboxGroup,
  type CheckboxGroupProps as ReactAriaCheckboxGroupProps,
} from "react-aria-components";
import { FieldError, Text } from "react-aria-components";

import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";

export interface CheckboxGroupProps extends Omit<ReactAriaCheckboxGroupProps, "children"> {
  children?: React.ReactNode;
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

export const CheckboxGroup = ({ label, description, errorMessage, children, ...props }: CheckboxGroupProps) => {
  return (
    <ReactAriaCheckboxGroup {...props}>
      <VStack spacing={2}>
        {label != undefined && (
          <HStack style={{ alignItems: "baseline" }}>
            <Label>{label}</Label>
            <Spacer />
            {!!props.isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
            {!props.isRequired && !props.isReadOnly && (
              <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
            )}
          </HStack>
        )}
        {description && (
          <Description
            data-testid="checkbox-field-description"
            className={clsx(formStyle.input_layout_column1, {
              [formStyle.description_with_tag]: label != undefined && (props.isReadOnly || !props.isRequired),
            })}
          >
            <Text slot="description">{description}</Text>
          </Description>
        )}
        <VStack spacing={3} style={{ marginTop: label != undefined || !!description ? 2 : 0, flex: 0, minWidth: 17 }}>
          {children}
        </VStack>
        {errorMessage != undefined && !!props.isInvalid && !props.isDisabled && (
          <div className={formStyle.error_message} data-testid="number-field-error">
            <FieldError>{errorMessage}</FieldError>
          </div>
        )}
      </VStack>
    </ReactAriaCheckboxGroup>
  );
};
